/**
 * stayai-no-decimals.js
 * Strips decimal portions from prices rendered by the Stay AI subscription widget.
 * Also forces one-time purchase for wholesale (B2B) customers.
 * Injects compare-at (strikethrough) price next to one-time purchase price
 * when the current variant has a compare_at_price.
 * Loaded as a deferred module so it never blocks page render.
 * Uses MutationObserver scoped to the widget container(s) to handle Stay AI's
 * async rendering and plan-selection re-renders. The active variant id itself
 * is tracked from the theme's own variant:update event rather than read live
 * from the DOM on every mutation, since the hidden variant-id input only
 * updates once the (potentially slow) variant fetch resolves — reading it
 * mid-fetch would use the previous variant's id and show a stale price.
 */

import { ThemeEvents } from '@theme/events';

const WIDGET_SELECTOR = '.stayai-widget, .stayai-subscriptions, purchase-options';
// The subscription option's radio input and its clickable label. Hidden for
// wholesale buyers, who can only purchase one-time.
const SUBSCRIPTION_OPTION_SELECTOR = '#purchaseTypeSubscription, [for="purchaseTypeSubscription"]';
// Grace period for Stay AI to finish rendering its options before we click one-time.
const ONE_TIME_SELECT_DELAY_MS = 100;
const PRICE_RE = /(\$[\d,]+)\.\d+/g;
// Wholesale buyers are identified by their B2B company attachment rather than by
// a customer tag, so the behaviour follows the company automatically and can't
// drift when someone forgets to tag an account. Catalogs aren't exposed to Liquid
// at all, and localization.market resolves to the buyer's geographic market
// ("us") rather than the Wholesale B2B market, so data-b2b (customer.b2b?) is the
// closest the theme can get to "this company buys from the Wholesale catalogue".
const IS_WHOLESALE = document.body.dataset.b2b === 'true';

function stripDecimals(root) {
	if (IS_WHOLESALE) return;
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	let node;
	while ((node = walker.nextNode())) {
		const updated = node.nodeValue.replace(PRICE_RE, '$1');
		if (updated !== node.nodeValue) node.nodeValue = updated;
	}
}

/**
 * Formats a price in cents to a human-readable string (e.g. 9500 → "$95").
 * Strips .00 decimals to match the widget's existing formatting.
 * @param {number} cents
 * @returns {string}
 */
function formatMoneyNoDecimals(cents) {
	if (IS_WHOLESALE) {
		return '$' + (cents / 100).toFixed(2);
	}
	const dollars = cents / 100;
	if (dollars === Math.floor(dollars)) {
		return '$' + dollars;
	}
	return '$' + dollars.toFixed(2).replace(/\.00$/, '');
}

// Tracks the currently-active variant id per <purchase-options> widget, kept
// in sync via the theme's variant:update event (see observeWidget) rather
// than read live from the DOM, since the hidden variant-id input lags behind
// the actual selection until its (potentially slow) fetch resolves.
const widgetVariantIds = new WeakMap();

/**
 * Resolves the <purchase-options> element for a given widget container.
 * @param {Element} container
 * @returns {Element | null}
 */
function resolvePurchaseOptions(container) {
	return container.matches('purchase-options') ? container : container.closest('purchase-options');
}

/**
 * Finds the product-form-component belonging to the same product as this
 * widget by walking up from the widget to the nearest ancestor that contains
 * one, rather than searching the whole document (which can match an
 * unrelated product-form-component elsewhere on the page, e.g. an
 * upsell/featured-product block or the quick-add modal).
 * @param {Element} purchaseOptions - The <purchase-options> element.
 * @returns {Element | null}
 */
function findScopedProductFormComponent(purchaseOptions) {
	let scope = purchaseOptions.parentElement;
	while (scope) {
		const component = scope.querySelector('product-form-component');
		if (component) return component;
		scope = scope.parentElement;
	}
	return null;
}

/**
 * Injects a compare-at (strikethrough) price beside the one-time purchase price
 * when the current variant has a compare_at_price greater than its price.
 * @param {Element} container - The widget container element.
 */
function injectCompareAtPrice(container) {
	const purchaseOptions = resolvePurchaseOptions(container);
	if (!purchaseOptions) return;

	const productDataStr = purchaseOptions.getAttribute('productdata');
	if (!productDataStr) return;

	/** @type {{ variants?: Array<{ id: number, price: number, compare_at_price: number|null }> } | null} */
	let productData;
	try {
		productData = JSON.parse(productDataStr);
	} catch (e) {
		return;
	}

	// Prefer the variant id tracked from the authoritative variant:update event
	// (see observeWidget); fall back to the Stay.ai currentvariantid attribute
	// only before that event has fired for this widget (e.g. right at page load).
	const targetVariantId = widgetVariantIds.get(purchaseOptions) ?? purchaseOptions.getAttribute('currentvariantid');
	if (!targetVariantId) return;

	const variant = productData?.variants?.find(
		(v) => String(v.id) === String(targetVariantId)
	);
	if (!variant) return;

	const compareAtPrice = variant.compare_at_price;
	const price = variant.price;

	// Walk up from container to find the one-time-price span (it may be in a child)
	const oneTimePriceEl = container.querySelector('[data-stayai-onetime-price]');
	if (!oneTimePriceEl) return;

	const existingWrapper = oneTimePriceEl.parentElement?.closest('.stayai-compare-at-wrapper');
	const existingCompareAt = existingWrapper?.querySelector('.stayai-compare-at');

	const shouldShowCompareAt = compareAtPrice && compareAtPrice > price;

	if (shouldShowCompareAt) {
		const formattedPrice = formatMoneyNoDecimals(compareAtPrice);

		// Already wrapped — just update the text if it changed (avoids infinite DOM mutation loop)
		if (existingWrapper && existingCompareAt) {
			if (existingCompareAt.textContent !== formattedPrice) {
				existingCompareAt.textContent = formattedPrice;
			}
			return;
		}

		const s = document.createElement('s');
		s.className = 'fullPrice stayai-compare-at';
		s.textContent = formattedPrice;
		s.style.setProperty('color', 'var(--stayai-selected-text-color)', 'important');
		s.style.setProperty('text-decoration-color', 'var(--stayai-selected-text-color)', 'important');
		s.style.setProperty('font-weight', 'bold', 'important');

		// Wrap both in a span so space-between doesn't push them apart
		const wrapper = document.createElement('span');
		wrapper.className = 'stayai-compare-at-wrapper';
		wrapper.style.display = 'inline-flex';
		wrapper.style.alignItems = 'center';
		oneTimePriceEl.insertAdjacentElement('beforebegin', wrapper);
		wrapper.appendChild(s);
		wrapper.appendChild(oneTimePriceEl);
	} else if (existingWrapper) {
		// Variant no longer has a compare-at — unwrap and clean up
		existingWrapper.parentElement?.insertBefore(oneTimePriceEl, existingWrapper);
		existingWrapper.remove();
	}
}

/**
 * For wholesale customers: hide the subscription option so the widget offers
 * only the one-time purchase. The widget itself stays visible — it still renders
 * the price and the one-time option.
 *
 * Injected once as a stylesheet rather than as an inline style per container so
 * that widgets mounting after this module runs are covered too, and so there is
 * no window in which the subscription option is visible before observeWidget
 * reaches it.
 *
 * setupWholesale still selects one-time and intercepts clicks underneath, so the
 * form state stays correct even if Stay AI re-renders the option.
 */
function hideSubscriptionOptionForWholesale() {
	if (!IS_WHOLESALE) return;
	const style = document.createElement('style');
	style.textContent = `${SUBSCRIPTION_OPTION_SELECTOR} { display: none !important; }`;
	document.head.appendChild(style);
}

/**
 * Selects the one-time purchase option for wholesale customers.
 *
 * Must run on every (re-)render rather than only at mount: Stay AI re-renders
 * the widget when the variant changes and can default back to subscription,
 * which would submit a selling plan the variant may not have. Hiding the option
 * with CSS and intercepting clicks only stops the *user* selecting it — neither
 * prevents Stay AI's own default.
 *
 * @param {Element} container - The widget container element.
 */
function selectOneTimePurchase(container) {
	if (!IS_WHOLESALE) return;

	// Delayed so Stay AI finishes (re-)rendering the options before we click
	setTimeout(() => {
		const oneTime = container.querySelector('#purchaseTypeOneTime');
		if (!oneTime) return;
		// Skip the click if it's already selected, so we don't kick off a
		// redundant re-render on every variant change.
		if (oneTime instanceof HTMLInputElement && oneTime.checked) return;
		if (oneTime instanceof HTMLElement) oneTime.click();
	}, ONE_TIME_SELECT_DELAY_MS);
}

/**
 * For wholesale customers: attach a delegated click listener to the widget
 * that intercepts clicks on the subscription option and clicks the
 * one-time purchase radio instead. Also selects one-time on initial render.
 * Still required even though the option is hidden via CSS, so that the underlying
 * form state is a one-time purchase rather than whatever Stay AI defaults to.
 */
function setupWholesale(container) {
	if (!IS_WHOLESALE) return;
	if (container.dataset.wholesaleHandled) return;
	container.dataset.wholesaleHandled = 'true';

	selectOneTimePurchase(container);

	// Intercept any click on the subscription option
	container.addEventListener('click', (e) => {
		const target = /** @type {HTMLElement} */ (e.target);
		if (target.closest(SUBSCRIPTION_OPTION_SELECTOR)) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			const oneTime = container.querySelector('#purchaseTypeOneTime');
			if (oneTime) oneTime.click();
		}
	}, true);
}

function observeWidget(container) {
	const purchaseOptions = resolvePurchaseOptions(container);
	if (purchaseOptions && !widgetVariantIds.has(purchaseOptions)) {
		const productFormComponent = findScopedProductFormComponent(purchaseOptions);

		// Seed the cache from the currently-rendered variant id. At this point
		// (page load, or before any variant switch) the hidden input reflects
		// the real selection, so this initial read is safe.
		const initialVariantId =
			productFormComponent?.querySelector('input[name="id"]')?.value ||
			purchaseOptions.getAttribute('currentvariantid');
		if (initialVariantId) widgetVariantIds.set(purchaseOptions, initialVariantId);

		const productId = productFormComponent?.dataset.productId;
		if (productId) {
			document.addEventListener(ThemeEvents.variantUpdate, (event) => {
				if (event.detail?.data?.productId !== productId) return;
				const newVariantId = event.detail.resource?.id;
				if (!newVariantId) return;
				widgetVariantIds.set(purchaseOptions, newVariantId);
				selectOneTimePurchase(container);
				stripDecimals(container);
				injectCompareAtPrice(container);
			});
		}
	}

	stripDecimals(container);
	setupWholesale(container);
	injectCompareAtPrice(container);

	let pending = false;
	new MutationObserver(() => {
		if (pending) return;
		pending = true;
		requestAnimationFrame(() => {
			stripDecimals(container);
			injectCompareAtPrice(container);
			pending = false;
		});
	}).observe(container, {
		childList: true,
		subtree: true,
		characterData: true,
		attributes: true,
		attributeFilter: ['currentvariantid', 'productdata'],
	});
}

function init() {
	hideSubscriptionOptionForWholesale();

	const containers = document.querySelectorAll(WIDGET_SELECTOR);

	if (containers.length) {
		containers.forEach(observeWidget);
		return;
	}

	// Widget not yet in DOM — watch body until it appears, then hand off
	const bodyObs = new MutationObserver(() => {
		const found = document.querySelectorAll(WIDGET_SELECTOR);
		if (!found.length) return;
		bodyObs.disconnect();
		found.forEach(observeWidget);
	});
	bodyObs.observe(document.body, { childList: true, subtree: true });
}

init();
